import { 
  FareRequest, 
  FareResponse, 
  FareErrorResponse,
  ExchangeRates,
  Currency 
} from '../types/fare';
import { API_BASE_URL } from '../config';
import { apiClient } from '../utils/apiClient';

/**
 * Calculate fare by calling the backend proxy
 * The backend proxy forwards to fare-validator service (URL hidden from frontend)
 */
export async function calculateFare(request: FareRequest): Promise<FareResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/fare/calculate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const data = await response.json();

    if (!response.ok) {
      const errorData = data as FareErrorResponse;
      throw new Error(errorData.message || 'Failed to calculate fare');
    }

    return data as FareResponse;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('An unexpected error occurred while calculating fare');
  }
}

/**
 * Check if the fare-validator service is running (via backend proxy)
 */
export async function checkBackendHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/fare/health`, {
      method: 'GET',
    });
    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * Convert fare amount from LKR to another currency
 */
export function convertCurrency(
  amountLKR: number, 
  targetCurrency: Currency,
  rates: ExchangeRates
): number {
  if (targetCurrency === 'LKR') {
    return amountLKR;
  }

  const rate = rates[targetCurrency];
  if (!rate) {
    return amountLKR;
  }

  return amountLKR * rate;
}

/**
 * Format price with commas and round to nearest 50
 */
export function formatPrice(price: number, roundTo50: boolean = true): string {
  const rounded = roundTo50 ? Math.round(price / 50) * 50 : Math.round(price);
  return rounded.toLocaleString('en-US');
}

/**
 * Format currency with symbol
 */
export function formatCurrencyAmount(
  amountLKR: number,
  currency: Currency,
  rates: ExchangeRates
): string {
  const symbols: Record<Currency, string> = {
    LKR: 'Rs.',
    USD: '$',
    GBP: '£',
    EUR: '€',
    CNY: '¥',
    RUB: '₽'
  };

  const converted = convertCurrency(amountLKR, currency, rates);
  const formatted = currency === 'LKR' 
    ? formatPrice(converted, true)
    : converted.toFixed(2);

  return `${symbols[currency]} ${formatted}`;
}

/**
 * Get currency name for display
 */
export function getCurrencyName(currency: Currency): string {
  const names: Record<Currency, string> = {
    LKR: 'Sri Lankan Rupee',
    USD: 'US Dollar',
    GBP: 'British Pound',
    EUR: 'Euro',
    CNY: 'Chinese Yuan',
    RUB: 'Russian Ruble'
  };
  return names[currency];
}

/**
 * Validate coordinates are within Sri Lanka bounds
 */
export function isWithinSriLanka(lat: number, lng: number): boolean {
  // Sri Lanka approximate bounds
  const bounds = {
    minLat: 5.916,
    maxLat: 9.835,
    minLng: 79.521,
    maxLng: 81.879
  };

  return (
    lat >= bounds.minLat &&
    lat <= bounds.maxLat &&
    lng >= bounds.minLng &&
    lng <= bounds.maxLng
  );
}

/**
 * Validate manual distance is within limits
 */
export function isValidManualDistance(distanceKm: number): boolean {
  return distanceKm > 0 && distanceKm <= 400;
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyError(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    
    if (message.includes('geo') || message.includes('sri lanka')) {
      return 'Location is outside Sri Lanka service area';
    }
    if (message.includes('distance') && message.includes('400')) {
      return 'Distance exceeds maximum limit of 400 km';
    }
    if (message.includes('network') || message.includes('fetch')) {
      return 'Unable to connect to fare calculator. Please check if the service is running.';
    }
    
    return error.message;
  }
  
  return 'An unexpected error occurred';
}

/**
 * Save fare calculation to user's history
 */
export async function saveFareHistory(userId: string, fareData: FareResponse): Promise<void> {
  try {
    const response = await apiClient.post('/api/fare/save-history', { userId, fareData });

    if (!response.ok) {
      // Don't throw - saving history is not critical
    }
  } catch (error) {
    // Don't throw - saving history is not critical
  }
}

/**
 * Load fare history for a user
 */
export async function loadFareHistory(userId: string, limit: number = 10): Promise<any[]> {
  try {
    const response = await apiClient.get(`/api/fare/history/${userId}?limit=${limit}`);
    
    if (!response.ok) {
      return [];
    }

    const data = await response.json();
    return data.history || [];
  } catch (error) {
    return [];
  }
}

